// SB6-ANA-FILT-07 — single source of truth for the *effective* AI class
// filter applied to every server query that can narrow by AI confidence.
//
// `useAiFilterStore.committedFilter` is the debounced `aiClassFilter` JSON
// string the AI Filter widget emits (the map `{ "<classUuid>": <minPct> }`
// over the CHECKED classes, or `null` when the sub-filter must not narrow).
// FILT-06 folded it into the data-points + facets queries; this hook lifts
// the exact gate those queries used so the List/Tiles/viewer findings
// surfaces (FILT-07) narrow with the **identical value at the identical
// instant** — there is no second serialisation path.
//
// The gate: the AI sub-filter only narrows when the AI kind is in scope at
// L3 of the global data-point selection (`selection.kind.includes("ai")`).
// That is the precise condition `useDataPointsQuery` / `useDataPointFacetsQuery`
// already use (queries.ts `effectiveAi`); reusing it keeps all surfaces +
// the data-point counts in agreement (the story's "consistency rule").
import { useAiFilterStore } from "./state/aiFilterStore.js";
import { useDataPointFilterStore } from "./state/dataPointFilterStore.js";

/**
 * The committed `aiClassFilter` JSON string to send, or `null` when the AI
 * sub-filter must not narrow (inactive kind, or nothing checked). Callers
 * pass it straight through to their query param — `null` ⇒ omit the param
 * (no narrowing, byte-identical to pre-FILT-07 requests).
 */
export function useEffectiveAiClassFilter(): string | null {
  const committedFilter = useAiFilterStore((s) => s.committedFilter);
  const kind = useDataPointFilterStore((s) => s.selection.kind);
  return kind.includes("ai") ? committedFilter : null;
}
